import inputmask from 'inputmask';
import Mediator from 'common/scripts/observer';
import Utils from 'common/scripts/utils';

const mediator = new Mediator();

class Input {
    init(options) {
        this.target = options.target || null;
        this.required = this._setRequired(options.required);
        this.state = !this.required;
        this.mode = options.mode;
        this.mask = options.mask || false;
        this.wrapper = this.target ? this.target.closest('.j-input-wrap') : null;
        this.form = this.target ? this.target.closest('form') : null;

        if (this.wrapper) {
            this.error = this.wrapper.querySelector('.j-input-error');
            this.buttonClear = this.wrapper.querySelector('.j-input-clear');
        }

        /* eslint-disable no-useless-escape */
        this.regexp = {
            name   : RegExp('^.{2,40}$'),
            message: RegExp(/^.{2,1500}$/s),
            phone  : RegExp('^((\\+7\\s)+([0-9]){3}\\-([0-9]){3}\\-([0-9]){2}-([0-9]){2})$'),
            email  : RegExp('^[-._a-zA-Za-яA-я0-9]{2,}@(?:[a-zA-Za-яА-Я0-9][-a-z-A-Z-a-я-А-Я0-9]+\\.)+[a-za-я]{2,6}$'),
            inn    : RegExp('\\d{4}\\s\\d{4}\\s(\\d{4}|\\d{2})$'),
            fileArh: RegExp('(\.doc|\.docx|\.rtf|\.pdf|\.wpd|\.txt)$')
        };
        /* eslint-enable */

        this.errorMessageEmpty = 'Заполните поле ввода';
        this.errorMessageOption = options.error;
        this.errorMessage = this.errorMessageOption;
        this.typeInput = this.target ? this.target.type : 'text';
        this.maxSizeFile = options.maxSizeFile || 4000000;

        this._setMode();
    }

    /**
     * Метод устанавливает является ли инпут обязательным к заполнению. По дефолту обязателен.
     * @param {boolean} option - опция из вне.
     * @return {boolean} true - обязателен, false - нет
     * @private
     */
    _setRequired(option) {
        return option !== false;
    }

    /**
     * Метод устанавливает режим валдиации для инпута
     */
    _setMode() {
        switch (this.mode) {
            case 'name':
                this._validateName();
                break;
            case 'message':
                this._validateMessage();
                break;
            case 'phone':
                this._validatePhone();
                break;
            case 'email':
                this._validateEmail();
                break;
            case 'agreement':
                this._validateAgreement();
                break;
            case 'inn':
                this._validateInn();
                break;
            case 'group':
                this._validateGroup();
                break;
            case 'file':
                this._validateFile();
                break;
            default:
                break;
        }
    }

    /**
     * Метод навешивает обработчики событий
     * @param {boolean} submit - флаг, если метод вызывается при самбите формы.
     * @private
     */
    _bindEvents(submit = false) {
        const handler = (event) => {
            const value = event.target.value;

            if (this.mode !== 'file') {
                this._validateLength(value);
                this._validate(value);
            }
        };

        if (submit && this.target) {
            this.target.addEventListener('input', handler, false);

            return;
        }

        if (this.target) {
            this.target.addEventListener('blur', (event) => {
                handler(event);

                this.target.addEventListener('input', handler, false);
            });
        }

        if (this.buttonClear) {
            this.buttonClear.addEventListener('click', () => {
                this.clear();
            });
        }
    }

    /**
     * Метод проверки в режиме 'name'.
     * @private
     */
    _validateName() {
        this.defRegexp = this.regexp.name;
        this._bindEvents();
    }

    /**
     * Метод проверки в режиме 'message'.
     * @private
     */
    _validateMessage() {
        this.defRegexp = this.regexp.message;
        this._bindEvents();
    }

    /**
     * Метод проверки в режиме 'phone'.
     * @private
     */
    _validatePhone() {
        this.defRegexp = this.regexp.phone;

        if (this.mask) {
            // Поставит первую 9 при первом фокусе
            let startValue = '';

            if (this.target) {
                this.target.addEventListener('focus', (event) => {
                    const value = event.target.value;

                    if (value === startValue) {
                        event.target.value = '9';
                        startValue = event.target.value;
                    }
                });

                this.target.addEventListener('blur', (event) => {
                    const value = event.target.value;

                    if (startValue === value) {
                        event.target.value = '';
                        startValue = '';
                    }
                });

                inputmask(this.mask).mask(this.target);
            }
        }

        this._bindEvents();
    }

    /**
     * Метод проверки в режиме 'email'.
     * @private
     */
    _validateEmail() {
        this.defRegexp = this.regexp.email;
        this._bindEvents();
    }

    /**
     * Метод проверки в режиме 'agreement'.
     * @private
     */
    _validateAgreement() {
        if (this.target) {
            this.target.addEventListener('change', (event) => {
                const target = event.target;

                if (target.checked) {
                    this.throwSuccess();
                } else {
                    this.throwError();
                }
            });
        }
    }

    /**
     * Метод проверки в режиме 'inn'.
     * @private
     */
    _validateInn() {
        this.defRegexp = this.regexp.inn;
        this._bindEvents();

        if (this.mask && this.target) {
            inputmask(this.mask).mask(this.target);
        }
    }

    /**
     * Метод проверяет длину значения инпута и записывает соотвествующий текст ошибки.
     * @param {string} value - значение текущего инпута
     */
    _validateLength(value) {
        if (value.length) {
            this.errorMessage = this.errorMessageOption;
        } else {
            this.errorMessage = this.errorMessageEmpty;
        }
    }

    /**
     * Метод проверяет расширение файла
     * @param {object} file - объект файла из инпута
     * @private
     */
    _validateExtensions(file) {
        if (!file) {
            return;
        }

        if (this.regexp.fileArh.test(file.name)) {
            this.throwSuccess();
        } else {
            this.errorMessage = 'Допускается загружать файлы только в форматах .doc, .docx, .rtf, .pdf, .wpd, .txt';
            this.throwError();
        }
    }

    /**
     * Метод проверяет размер файла
     * @param {object} file - объект файла из инпута
     * @private
     */
    _validateFileSize(file) {
        // если формат файла не верный, то на размер проверку не проводим
        if (!file || !this.state) {
            return;
        }

        if (file.size <= this.maxSizeFile) {
            this.throwSuccess();
        } else {
            this.errorMessage = 'Превышен размер файла, максимальный размер 4 Мб';
            this.throwError();
        }
    }

    /**
     * Метод проверяет группу инпутов на хоть одно чекнутое состояние.
     */
    _validateGroup() {
        if (!this.target) return;

        const input = Array.from(this.target.querySelectorAll('input'));

        // если хоть один элемент массива не чекнут, то общее состояние = false
        const checkState = () => {
            return input.some((item) => {
                return item.checked;
            });
        };

        if (checkState()) {
            this.state = true;
        }

        input.forEach((item) => {
            item.addEventListener('change', () => {
                return checkState() ? this.throwSuccess() : this.throwError();
            });
        });
    }

    /**
     * Метод проверки в режиме 'file'.
     * @private
     */
    _validateFile() {
        if (!this.target) return;

        this.target.addEventListener('change', () => {
            const first = 0;
            const file = this.target.files[first];

            this._validateExtensions(file);
            this._validateFileSize(file);

            if (this.state) {
                this.target.value = file.name;
            } else {
                this.target.value = '';
            }
        });
    }

    /**
     * Метод проверки соответствия регулярного выражения и значения инпута.
     * @param {string} value - значение инпута.
     * @private
     */
    _validate(value) {
        if (!this.required && !value.length) {
            this.throwSuccess(true);

            return;
        }

        if (this.defRegexp.test(value)) {
            this.throwSuccess();
        } else {
            this.throwError();
        }

        mediator.publish('inputValidate', this);
    }

    /**
     * Метод устанавливает положительный результат валидации, и скрывает сообщение об ошибке.
     * @param {boolean} remove - флаг обозначающий, что нужно удалить все следы валидации.
     * @public
     */
    throwSuccess(remove = false) {
        this.state = true;

        if (this.wrapper) {
            this.wrapper.classList.remove('has-error');
            this.wrapper.classList.add('has-success');
        }

        if (remove) {
            this._removeValidate();
        }
    }

    /**
     * Метод устанавливает отрицательный результат валидации, и показывает сообщение об ошибке.
     * @param {HTMLInputElement} item - инпут который не прошел валидация при сабмите.
     * @public
     */
    throwError(item = null) {
        this.state = false;

        if (this.wrapper) {
            this.wrapper.classList.add('has-error');
            this.wrapper.classList.remove('has-success');
        }

        if (this.target && this.mode !== 'agreement' && this.mode !== 'group' && this.mode !== 'file') {
            this._validateLength(this.target.value);
            this._bindEvents(true);
        }

        if (this.target && this.mode === 'agreement') {
            this.target.classList.add('has-error');
            this.target.classList.remove('has-success');
        }

        Utils.clearHtml(this.error);
        Utils.insetContent(this.error, this.errorMessage);
    }

    /**
     * Скрывает визуальные следы валидации
     * @private
     */
    _removeValidate() {
        if (this.wrapper) {
            this.wrapper.classList.remove('has-error');
            this.wrapper.classList.remove('has-success');
        }
    }

    /**
     * Очищает инпут и сбрасывает валидацию
     * @public
     */
    clear() {
        if (this.mode === 'phone' && this.target) {
            this.target.value = '9';
        } else if (this.target) {
            this.state = false;
            this.target.value = '';
        }

        this._removeValidate(true);
    }
}

export default Input;
